import React, {
  FC,
  PropsWithChildren,
  ReactText,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useSelector } from 'react-redux';
import { Undefinable } from '@symfa-inc/providence-types';
import { HttpService } from '@core/services';
import { PaceTaskHttpService } from '@core/services/http';
import { toggleModal } from '@core/utils/methods';
import { PaceTask, TabComponentProps } from '@models/interfaces';
import { DeleteModal, PaceTasks, Search } from '@shared/components';
import { PrimaryButton } from '@shared/modules';
import { PaceTaskSelectors } from '@store/selectors';
import { AccessPaceTaskModal, AddAndEditPaceTaskModal } from '../../modals';
import { getAllPaceTaskColumns } from './models/constants';

import './styles.scss';

export const AllPaceTasks: FC<TabComponentProps> = ({
  isActive,
}: PropsWithChildren<TabComponentProps>) => {
  const paceTaskService = useMemo(
    () => HttpService.getHttpRequests(PaceTaskHttpService),
    [],
  );

  const [selectedRows, setSelectedRows] = useState<ReactText[]>([]);
  const [isAddModalVisible, setAddModalVisible] = useState<boolean>(false);
  const [isAccessModalVisible, setAccessModalVisible] =
    useState<boolean>(false);
  const [deleteModalVisible, setDeleteModalVisible] = useState<boolean>(false);
  const [selectedItem, setSelectedItem] = useState<PaceTask>();
  const [search, setSearch] = useState<string>('');
  const [editablePaceTak, setEditablePaceTask] =
    useState<Undefinable<PaceTask>>();

  const rowSelection = {
    selectedRowKeys: selectedRows,
    onChange: (rows: ReactText[]): void => setSelectedRows(rows),
  };

  const toggleAddTaskModal = (): void => {
    setAddModalVisible(!isAddModalVisible);

    if (isAddModalVisible && editablePaceTak) {
      setEditablePaceTask(undefined);
    }
  };
  const toggleAccessModal = (): void =>
    setAccessModalVisible(!isAccessModalVisible);
  const editPaceTask = (paceTask: PaceTask): void => {
    setEditablePaceTask(paceTask);
    toggleAddTaskModal();
  };

  const deletePaceTask = async (): Promise<void> => {
    if (selectedItem) {
      await paceTaskService.deletePaceTask(selectedItem.id);
      setSelectedItem(undefined);
    }

    setDeleteModalVisible(false);
  };

  const onDeletePaceTaskBtn = (paceTask: PaceTask): void => {
    setSelectedItem(paceTask);
    setDeleteModalVisible(true);
  };

  const paceTaskData = useSelector(PaceTaskSelectors.getAllPaceTasks);

  useEffect(() => {
    if (isActive) {
      paceTaskService.getAllPaceTasks(search);
    }
  }, [isActive, search]);

  useEffect(() => {
    if (selectedRows.length) {
      setSelectedRows(
        selectedRows.filter(
          (id: ReactText): boolean =>
            !!paceTaskData.find((p: PaceTask) => p.id === id),
        ),
      );
    }
  }, [paceTaskData]);

  return (
    <div className="prov-all-pace-tasks">
      <AddAndEditPaceTaskModal
        paceTask={editablePaceTak}
        visible={isAddModalVisible}
        onOk={toggleAddTaskModal}
        onCancel={toggleAddTaskModal}
      />
      <AccessPaceTaskModal
        paceTaskId={selectedRows as string[]}
        visible={isAccessModalVisible}
        onOk={toggleAccessModal}
        onCancel={toggleAccessModal}
      />
      <PaceTasks
        columns={getAllPaceTaskColumns(editPaceTask, onDeletePaceTaskBtn)}
        data={paceTaskData}
        infoRowLeftElement={
          <Search onChange={(value: string): void => setSearch(value)} />
        }
        infoRowRightElement={
          <PrimaryButton
            title="Add Task"
            icon="+"
            onClick={toggleAddTaskModal}
          />
        }
        rowSelection={rowSelection}
      />
      <DeleteModal
        onOk={deletePaceTask}
        onCancel={toggleModal(setDeleteModalVisible)}
        visible={deleteModalVisible}
      />
      <PrimaryButton
        disabled={!selectedRows.length}
        title="Assign Access"
        icon="+"
        onClick={toggleAccessModal}
        className="prov-all-pace-tasks__access-btn"
      />
    </div>
  );
};
